import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import CodeViewer from "@/components/CodeViewer";
import { GitFork, ArrowLeft } from "lucide-react";


export default function PublicStrategy() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/gallery/strategies/${slug}`);
      setDoc(data);
    } catch { toast.error("Public strategy not found"); }
    finally { setLoading(false); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const doFork = async () => {
    if (!user) { toast.error("Log in to fork"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/strategies/${doc.strategy_id}/fork`);
      toast.success("Forked → " + data.name);
      window.location.href = `/builder/${data.strategy_id}`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Fork failed"); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-[var(--bg-dark)]"><Navbar /><div className="p-10 text-[var(--text-muted)] font-mono">Loading…</div></div>;
  }
  if (!doc) {
    return (
      <div className="min-h-screen bg-[var(--bg-dark)]" data-testid="public-not-found">
        <Navbar/>
        <div className="max-w-lg mx-auto p-10 text-center">
          <h1 className="font-display text-3xl text-white">404 — Not Public</h1>
          <p className="text-[var(--text-muted)] mt-2">This strategy isn't published or was removed.</p>
          <Link to="/gallery" className="btn-primary mt-6 inline-flex">Back to gallery</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-dark)]" data-testid="public-strategy-page">
      <Navbar/>
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <Link to="/gallery" className="btn-outline inline-flex items-center gap-2 text-xs mb-6"><ArrowLeft size={13}/> Gallery</Link>
        <header className="mb-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="chip">{(doc.platform || "mt4").toUpperCase()}</span>
            <span className="chip">{doc.config?.symbol || "EURUSD"}</span>
            <span className="chip">{(doc.config?.timeframe || "").replace("PERIOD_","")}</span>
            <span className="chip"><GitFork size={11} className="inline mr-1"/>{doc.fork_count || 0}</span>
          </div>
          <h1 className="font-display text-4xl text-white">{doc.name}</h1>
          <p className="text-[var(--text-muted)] mt-2 text-sm">Published by <span className="text-white">{doc.author?.name}</span></p>
        </header>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <CodeViewer code={doc.mql4_code || ""} title={(doc.name || "Strategy") + "." + ((doc.platform === "mt5") ? "mq5" : "mq4")}/>
          </div>
          <aside className="space-y-4">
            <div className="card-sharp p-5">
              <h2 className="font-display text-lg text-white mb-3">Rules</h2>
              <ul className="text-xs font-mono space-y-1 text-[var(--text-secondary)]" data-testid="public-rules-list">
                <li className="text-[var(--text-muted)] uppercase tracking-widest text-[10px]">Entry ({(doc.config?.entry_rules || []).length})</li>
                {(doc.config?.entry_rules || []).map((r, i) => (
                  <li key={r._rid || `e-${i}`}>· {r.direction?.toUpperCase()} {r.indicator} {r.operator} {r.value ?? r.reference ?? ""}</li>
                ))}
                <li className="text-[var(--text-muted)] uppercase tracking-widest text-[10px] mt-3">Exit ({(doc.config?.exit_rules || []).length})</li>
                {(doc.config?.exit_rules || []).map((r, i) => (
                  <li key={r._rid || `x-${i}`}>· {r.direction?.toUpperCase()} {r.indicator} {r.operator} {r.value ?? r.reference ?? ""}</li>
                ))}
              </ul>
            </div>
            <button data-testid="public-fork-btn"
              onClick={doFork} disabled={busy}
              className="btn-primary w-full flex items-center justify-center gap-2 py-3">
              <GitFork size={15}/> {busy ? "Forking…" : "Fork to my library"}
            </button>
            {!user && (
              <p className="text-[var(--text-muted)] text-xs font-mono text-center">
                <Link to="/login" className="text-[var(--accent)] hover:underline">Log in</Link> to fork this strategy
              </p>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
